import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';

import { RouterLink } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Shared in-app 404 page (#1257, #1443, #1441).
 *
 * When the visitor is SIGNED IN, a dead URL is almost always a stale/mistyped
 * deep link rather than a sign-in problem — so we surface in-app recovery CTAs
 * ("Back to dashboard", "Browse the calendar") that route them somewhere real,
 * instead of dead-ending on the support mailto. The mailto stays as the only
 * affordance for ANONYMOUS visitors, who can't be routed into the member tree.
 *
 * Auth state is read from Auth0's `AuthService.isAuthenticated$`, the same
 * source the shared nav uses (see `nav.component.ts`).
 *
 * The recovery targets are `input()`s rather than hardcoded routes: this is a
 * shared component mounted across apps whose route trees differ, so each host
 * passes its own real paths. The defaults are the customer-portal routes.
 */
@Component({
  selector: 'bodyanalytics-page-not-found',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  templateUrl: './page-not-found.component.html',
  styleUrl: './page-not-found.component.scss',
})
export class PageNotFoundComponent {
  /** Primary recovery CTA target — the signed-in member's home surface. */
  readonly dashboardLink = input<string>('/auth/dashboard');

  /** Secondary recovery CTA target — where members browse/manage bookings. */
  readonly browseLink = input<string>('/auth/my-bookings');

  /**
   * `undefined` until Auth0 resolves the session, then `true`/`false`. Treated
   * as anonymous (mailto-only) until it is explicitly `true`.
   */
  protected readonly isAuthenticated = toSignal(inject(AuthService).isAuthenticated$);
}
